import React, { useEffect, useRef, useState } from 'react';
import { FileSearch, Loader2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import EmptyState from '../Common/EmptyState';
import { userFacingError } from '../../i18n/userFacingError';

function unwrapConsulta(payload) {
  const items = Array.isArray(payload)
    ? payload
    : payload?.items ?? payload?.data ?? [];
  return {
    items,
    found: payload?.found ?? items.length > 0,
    query: payload?.query || '',
    total: payload?.total ?? items.length,
    message: payload?.message || '',
  };
}

/**
 * Busca pública com estados de idle / inválido / carregando / vazio / resultado.
 */
const ConsultaPublicaSearch = ({
  title,
  description,
  placeholder,
  submitLabel = 'Consultar',
  entityLabel = 'registro',
  minLength = 2,
  initialQuery = '',
  search,
  renderItem,
  renderResults,
  extraEmpty,
  idleHint,
  autoSearch = false,
  onSearched,
}) => {
  const [q, setQ] = useState(initialQuery);
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState({ items: [], query: '', message: '', total: 0 });
  const autoRan = useRef(false);

  useEffect(() => {
    if (initialQuery) setQ(initialQuery);
  }, [initialQuery]);

  const runSearch = async (raw) => {
    const needle = String(raw || '').trim();
    if (needle.length < minLength) {
      setStatus('invalid');
      setResult({ items: [], query: needle, message: '', total: 0 });
      return;
    }
    setStatus('loading');
    try {
      const payload = await search(needle);
      const next = unwrapConsulta(payload);
      next.query = next.query || needle;
      setResult(next);
      setStatus(next.items.length ? 'results' : 'empty');
      onSearched?.(needle, next);
    } catch (e) {
      setStatus('error');
      setResult({ items: [], query: needle, message: '', total: 0 });
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  useEffect(() => {
    if (!autoSearch || autoRan.current || !initialQuery) return;
    autoRan.current = true;
    runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, initialQuery]);

  const buscar = async (event) => {
    event.preventDefault();
    await runSearch(q);
  };

  return (
    <div>
      {title ? <h2 className="text-base font-semibold text-gray-900">{title}</h2> : null}
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={buscar}>
        <label className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <Search className="h-4 w-4" aria-hidden />
          </span>
          <input
            className="form-input pl-10"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            minLength={minLength}
          />
        </label>
        <button type="submit" className="btn btn-primary sm:min-w-[140px]" disabled={status === 'loading'}>
          {status === 'loading' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Consultando…
            </>
          ) : (
            submitLabel
          )}
        </button>
      </form>

      <div className="mt-5" aria-live="polite">
        {status === 'idle' ? (
          <p className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-3 text-sm text-gray-600">
            <Search className="h-4 w-4 shrink-0 text-primary-700" aria-hidden />
            {idleHint || `Informe o número e clique em Consultar para ver o andamento neste órgão.`}
          </p>
        ) : null}

        {status === 'invalid' ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Digite pelo menos {minLength} caracteres do número do {entityLabel} para buscar.
          </p>
        ) : null}

        {status === 'loading' ? (
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Buscando ocorrências de {entityLabel}…
          </p>
        ) : null}

        {status === 'error' ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            Não foi possível consultar agora. Confira a conexão e tente de novo com o mesmo número.
          </p>
        ) : null}

        {status === 'empty' ? (
          <EmptyState
            icon={FileSearch}
            title="Nenhuma ocorrência encontrada"
            description={
              result.message ||
              `Não há ${entityLabel} com «${result.query}» neste órgão. Confira o número e o ano. Esta consulta não pesquisa SEI, DOU nem IBAMA.`
            }
            action={
              <div className="space-y-2 text-left text-sm text-gray-600">
                <p className="font-medium text-gray-800">O que fazer agora</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Confira dígitos, barras e o ano do protocolo.</li>
                  <li>Use o número deste órgão (useAmbiental), não o número do SEI/IBAMA.</li>
                  <li>Se o pedido for de outro município, consulte o órgão responsável.</li>
                  <li>Ainda em dúvida, fale com o protocolo do órgão com o número que você digitou.</li>
                </ul>
                {extraEmpty}
              </div>
            }
          />
        ) : null}

        {status === 'results' ? (
          <div>
            <p className="mb-3 text-sm text-gray-600">
              {entityLabel === 'denúncia'
                ? result.total === 1
                  ? '1 denúncia encontrada neste órgão'
                  : `${result.total} denúncias encontradas neste órgão`
                : result.total === 1
                  ? `1 ${entityLabel} encontrado neste órgão`
                  : `${result.total} ${entityLabel}s encontrados neste órgão`}
              {result.query ? (
                <span className="text-gray-400">
                  {' '}
                  para <span className="font-mono text-gray-600">«{result.query}»</span>
                </span>
              ) : null}
            </p>
            {renderResults ? (
              renderResults(result.items, result)
            ) : (
              <ul className="space-y-2 text-sm">
                {result.items.map((row, idx) => (
                  <li key={row.id || row.numero || row.protocolo || idx} className="rounded-lg border border-gray-100 px-3 py-2">
                    {renderItem(row)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ConsultaPublicaSearch;
